import { useAuth } from "@/lib/auth";
import { Key, Puzzle, Users, Shield } from "lucide-react";

const INTEGRATIONS = [
  { name: "VirusTotal", key: "VIRUSTOTAL_API_KEY", status: "mocked", desc: "IP / domain / URL / hash intelligence." },
  { name: "Hybrid Analysis", key: "HYBRID_ANALYSIS_API_KEY", status: "mocked", desc: "Malware sandbox verdicts and family classification." },
  { name: "ForensiQ AI", key: "EMERGENT_LLM_KEY", status: "live", desc: "AI root-cause & remediation engine." },
  { name: "Slack (ChatOps)", key: "SLACK_WEBHOOK", status: "coming_soon", desc: "Push alert & incident notifications." },
  { name: "Jira / ServiceNow", key: "TICKETING_URL", status: "coming_soon", desc: "Automatic ticket creation & sync." },
];

const STATUS_STYLE = {
  live:        { fg: "#34C759", label: "LIVE" },
  mocked:      { fg: "#FFCC00", label: "MOCKED" },
  coming_soon: { fg: "#8E9196", label: "COMING SOON" },
};

export default function Settings() {
  const { user } = useAuth();

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Platform configuration</div>
        <h1 className="font-display font-black text-5xl mt-2">Settings</h1>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Profile */}
        <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="profile-card">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> Profile
          </div>
          <div className="font-display text-xl mt-1 mb-4">You</div>
          <div className="space-y-2 font-mono text-sm">
            <div className="flex justify-between border-b border-white/5 pb-1"><span className="text-[#8E9196]">Name</span><span>{user?.name}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-1"><span className="text-[#8E9196]">Email</span><span>{user?.email}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-1"><span className="text-[#8E9196]">Role</span><span className="uppercase text-[#00F0FF]">{user?.role}</span></div>
            <div className="flex justify-between"><span className="text-[#8E9196]">Session</span><span className="text-[#34C759]">healthy</span></div>
          </div>
        </div>

        {/* Auth */}
        <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="auth-card">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> Authentication
          </div>
          <div className="font-display text-xl mt-1 mb-4">JWT · role-based</div>
          <ul className="space-y-2 font-mono text-xs text-[#8E9196]">
            <li>• Tokens signed with HS256, 24-hour expiry</li>
            <li>• Passwords hashed with bcrypt (cost 12)</li>
            <li>• Roles: analyst, admin (admin required for compliance scans)</li>
            <li>• All /api/* endpoints require Authorization header</li>
          </ul>
        </div>

        {/* Integrations */}
        <div className="md:col-span-2 border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="integrations-card">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Puzzle className="w-3.5 h-3.5" /> Integrations & API keys
          </div>
          <div className="font-display text-xl mt-1 mb-4">Multi-function integration</div>

          <div className="space-y-2">
            {INTEGRATIONS.map((i) => {
              const s = STATUS_STYLE[i.status];
              return (
                <div key={i.name} className="flex items-center gap-4 border border-white/5 rounded-sm p-3 hover:border-white/15" data-testid={`integration-${i.name.replace(/\s/g,'-')}`}>
                  <Key className="w-4 h-4 text-[#8E9196]" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{i.name}</div>
                    <div className="text-xs text-[#8E9196]">{i.desc}</div>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest border rounded-sm px-2 py-0.5"
                    style={{ color: s.fg, borderColor: s.fg + "55" }}>{s.label}</span>
                  <code className="font-mono text-[10px] text-[#8E9196] hidden md:inline">{i.key}</code>
                </div>
              );
            })}
          </div>

          <div className="mt-4 text-xs text-[#8E9196]">
            To swap mocked feeds for live data, add API keys to <code className="font-mono text-[#00F0FF]">/app/backend/.env</code> and restart the backend.
          </div>
        </div>

        <div className="md:col-span-2 border border-[#00F0FF]/40 bg-[#00F0FF]/[0.03] rounded-sm p-5" data-testid="developer-card">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF]">Developer</div>
          <div className="font-display text-2xl mt-1">Built by Ahamed Fasudeen</div>
          <p className="text-sm text-[#E5E5E5] mt-2">
            ForensiQ is an investigation-first cyber-defense platform: it fuses six threat-intel sources,
            reconstructs the IOC&apos;s history via the Time Machine, and produces court-ready PDF reports
            with a SHA-256 chain-of-custody fingerprint.
          </p>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              ["Enrichment", "6 sources"],
              ["AI", "ForensiQ AI"],
              ["Frameworks", "5 compliance"],
              ["Report", "Signed PDF"],
            ].map(([k, v]) => (
              <div key={k} className="border border-white/10 rounded-sm p-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">{k}</div>
                <div className="font-mono text-sm text-white">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
